import { useCallback, useState } from 'react'

import { RequestStatus, type ListResults } from 'core/domain/Request'

import sentry from 'legacy/utils/sentry'

import { type AgnosticShipment } from '../../domain/AgnosticShipment'
import { type AgnosticShipmentFilter } from '../../domain/AgnosticShipmentFilter'
import { type AgnosticShipmentSort } from '../../domain/AgnosticShipmentSort'
import { getAgnosticShipmentsList } from '../../use-cases/getAgnosticShipmentsList'

type State = {
  status: RequestStatus
  shipments: ListResults<AgnosticShipment>
  requests: number
}

function getInitialState(): State {
  return {
    status: RequestStatus.Initial,
    shipments: { count: 0, next: null, previous: null, results: [] },
    requests: 0,
  }
}

export function useAgnosticShipmentsList() {
  const [{ status, shipments, requests }, setState] = useState<State>(getInitialState)

  const fetchAllShipments = useCallback(
    async (
      filter?: AgnosticShipmentFilter,
      sort?: AgnosticShipmentSort,
      page?: number,
      abortController?: AbortController,
      enableNewShipmentsEndpoint?: boolean
    ) => {
      try {
        setState(previous => ({
          ...previous,
          status: RequestStatus.Pending,
        }))

        const results = await getAgnosticShipmentsList(
          {
            config: abortController,
            filter,
            sort,
            page,
          },
          Boolean(enableNewShipmentsEndpoint)
        )

        setState(previous => {
          return {
            shipments: results,
            status: RequestStatus.Done,
            requests: previous.requests + 1,
          }
        })
      } catch (error) {
        if (!abortController?.signal?.aborted) {
          setState(previous => ({
            ...previous,
            status: RequestStatus.Error,
          }))
          sentry.log(error)
        }
      }
    },
    []
  )

  const isDone = status === RequestStatus.Done
  const isDirty = requests > 1

  return {
    status,
    shipments,
    isDone,
    isDirty,
    fetchAllShipments,
  }
}
